import dayjs from "dayjs"
import { useInView } from "framer-motion"
import * as React from "react"
import styled from "styled-components"
import { Spacer } from "../atoms/spacers"
import NotificationSymbol from "../atoms/NotificationSymbol"
import { useSetUserLogAsRead } from "../../hooks/useSetUserLogAsRead"
import { useSetGroupLogAsRead } from "../../hooks/useSetGroupLogAsRead"
import { MediaQuery } from "../../utils/design-helpers"
import { Col, Hidden, Visible } from "react-grid-system"
import Body from "../atoms/typography/Body"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  read: boolean
  date: any
  message: string
  id: number | string
  isUserAction: boolean
}

const Bar = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
  border-radius: ${(props) => props.theme.borders.softRadius};
  display: flex;
  align-items: center;

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }

  ${MediaQuery.XS} {
    padding: ${(props) => props.theme.spacings.large};

    :hover {
      background-color: transparent;
    }
  }
`

const NotificationBar: React.FunctionComponent<Props> = ({ read, date, message, id, isUserAction }) => {
  const ref = React.useRef(null)
  const isInView = useInView(ref, { once: true })

  const onCompletedMarkLogAsRead = () => {
    console.log("Log marked as read")
  }
  const onErrorMarkLogAsRead = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error marking log as read")
  }
  const { setUserLogAsRead } = useSetUserLogAsRead({
    onCompleted: onCompletedMarkLogAsRead,
    onError: onErrorMarkLogAsRead,
  })
  const { setGroupLogAsRead } = useSetGroupLogAsRead({
    onCompleted: onCompletedMarkLogAsRead,
    onError: onErrorMarkLogAsRead,
  })
  const onMarkUserLogAsRead = async () => {
    try {
      await setUserLogAsRead({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error marking user log as read")
    }
  }
  const onMarkGroupLogAsRead = async () => {
    try {
      await setGroupLogAsRead({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error marking group log as read")
    }
  }

  React.useEffect(() => {
    if (!read) {
      if (isInView) {
        if (isUserAction) {
          onMarkUserLogAsRead()
        } else {
          onMarkGroupLogAsRead()
        }
      }
    }
  }, [isInView])

  return (
    <Bar ref={ref}>
      <Visible xs>
        <Col md={1} xs={1}>
          <NotificationSymbol read={read} />
        </Col>
      </Visible>
      <Hidden xs>
        <NotificationSymbol read={read} />
        <Spacer spacing={0.5} />
        <Body>{dayjs(date).format("DD MMM YYYY")}:</Body>
      </Hidden>
      <Spacer spacing={0.5} />
      <Body>{message}</Body>
    </Bar>
  )
}

export default NotificationBar
